import { createContext, useContext, useEffect, useState, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react';

interface Loc { path: string; query: URLSearchParams }
const read = (): Loc => ({ path: window.location.pathname.replace(/\/+$/, '') || '/', query: new URLSearchParams(window.location.search) });
const RouterContext = createContext<Loc>(read());

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (/^https?:\/\//.test(to) && !to.startsWith(window.location.origin)) { window.location.href = to; return; }
  window.history[opts.replace ? 'replaceState' : 'pushState'](null, '', to.replace(window.location.origin, ''));
  window.dispatchEvent(new Event('gs:navigate'));
  if (!opts.replace) window.scrollTo(0, 0);
}

export function setQuery(values: Record<string, string | null>): void {
  const q = new URLSearchParams(window.location.search);
  for (const [k, v] of Object.entries(values)) { if (v === null || v === '') q.delete(k); else q.set(k, v); }
  const qs = q.toString();
  navigate(`${window.location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [loc, setLoc] = useState<Loc>(read);
  useEffect(() => {
    const update = (): void => setLoc(read());
    window.addEventListener('popstate', update);
    window.addEventListener('gs:navigate', update);
    return () => { window.removeEventListener('popstate', update); window.removeEventListener('gs:navigate', update); };
  }, []);
  return <RouterContext.Provider value={loc}>{children}</RouterContext.Provider>;
}

export const useLocation = (): Loc => useContext(RouterContext);

export function matchPath(pattern: string, path: string): Record<string, string> | null {
  const keys: string[] = [];
  const re = new RegExp(`^${pattern.replace(/\/:(\w+)/g, (_m, k: string) => { keys.push(k); return '/([^/]+)'; })}$`);
  const m = re.exec(path);
  return m ? Object.fromEntries(keys.map((k, i) => [k, decodeURIComponent(m[i + 1])])) : null;
}

export function Link({ to, children, onClick, ...rest }: { to: string; children: ReactNode } & AnchorHTMLAttributes<HTMLAnchorElement>) {
  const handle = (e: MouseEvent<HTMLAnchorElement>): void => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || rest.target === '_blank') return;
    e.preventDefault();
    navigate(to);
  };
  return <a href={to} onClick={handle} {...rest}>{children}</a>;
}
